import { useState } from 'react';
import client from '../api/client';
import './ReferralPage.css';

function ReferralPage() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    organization: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    referralSource: '',
    referralCode: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      await client.post('/referrals/apply', formData);
      setSuccess(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        organization: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        referralSource: '',
        referralCode: '',
        message: '',
      });
    } catch (err: any) {
      setError(
        err.response?.data?.error || 'Something went wrong. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const US_STATES = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
    'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
    'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
    'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
    'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  ];

  return (
    <div className="container referral-page">
      <div className="referral-hero">
        <h1>Earn With GetSkimp</h1>
        <p className="referral-hero-subtitle">
          Partner with GetSkimp and earn commission for every person you refer.
          Whether you're a property
          manager, real estate agent, or just well-connected, there's no cost
          to join and no limit on what you can earn.
        </p>
        <a href="#apply" className="btn btn-primary btn-lg">
          Join the Referral Program
        </a>
      </div>

      <div className="referral-features grid grid-3">
        <div className="card feature-card">
          <div className="feature-icon">🤝</div>
          <h3>Refer Clients</h3>
          <p>
            Send your clients, tenants, or contacts to GetSkimp for renters
            insurance quotes.
          </p>
        </div>

        <div className="card feature-card">
          <div className="feature-icon">💸</div>
          <h3>Earn Commission</h3>
          <p>
            Get paid for every referral that you send us.
          </p>
        </div>

        <div className="card feature-card">
          <div className="feature-icon">🚀</div>
          <h3>No Cap</h3>
          <p>
            There's no limit on how many people you can refer or how much you
            can earn. The more you share, the more you make.
          </p>
        </div>
      </div>

      <div className="referral-how-it-works">
        <h2>How It Works</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <h4>Apply</h4>
            <p>Fill out the form below to join our referral program</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h4>Get Approved</h4>
            <p>We'll review your application and get you set up</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h4>Share</h4>
            <p>Send your unique referral link to anyone who needs coverage</p>
          </div>
          <div className="step">
            <div className="step-number">4</div>
            <h4>Get Paid</h4>
            <p>Earn commission once your referral fills out a quote and meets our criteria</p>
          </div>
        </div>
      </div>

      <div className="referral-form-section" id="apply">
        {success ? (
          <div className="card referral-success-card">
            <div className="referral-success-content">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
              <h2>Application Submitted!</h2>
              <p>
                Thanks for your interest in the GetSkimp referral program. We'll
                review your application and reach out within 1–2 business days.
              </p>
              <button
                className="btn btn-primary"
                style={{ marginTop: '1.5rem' }}
                onClick={() => setSuccess(false)}
              >
                Submit Another Application
              </button>
            </div>
          </div>
        ) : (
          <div className="card referral-form-card">
            <div className="card-header">Join the Referral Program</div>
            <p style={{ color: '#4B5563', marginBottom: '1.5rem' }}>
              Fill out the form below to apply. We'll review your information
              and get back to you shortly.
            </p>

            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-error">{error}</div>}

              <div className="referral-form-grid">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="John"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe"
                    required
                  />
                </div>
              </div>

              <div className="referral-form-grid">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="organization">Organization / Business Name</label>
                <input
                  id="organization"
                  name="organization"
                  type="text"
                  value={formData.organization}
                  onChange={handleChange}
                  placeholder="Your company or organization (if applicable)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address">Street Address</label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="123 Main St"
                />
              </div>

              <div className="referral-form-grid referral-form-grid-3">
                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Houston"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="state">State</label>
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  >
                    <option value="">Select State</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="zipCode">Zip Code</label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    maxLength={5}
                    value={formData.zipCode}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        zipCode: e.target.value.replace(/\D/g, ''),
                      }))
                    }
                    placeholder="77001"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="referralSource">
                  How did you hear about us?
                </label>
                <select
                  id="referralSource"
                  name="referralSource"
                  value={formData.referralSource}
                  onChange={handleChange}
                >
                  <option value="">Select one</option>
                  <option value="search">Search Engine</option>
                  <option value="social">Social Media</option>
                  <option value="friend">Friend or Colleague</option>
                  <option value="property_manager">Property Manager</option>
                  <option value="real_estate">Real Estate Agent</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="referralCode" style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  Referrer's Email
                  <span
                    title="If someone referred you to the GetSkimp referral program, enter their email."
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--color-grey)',
                      color: '#4B5563',
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      cursor: 'help',
                    }}
                  >
                    ?
                  </span>
                </label>
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={handleChange}
                  placeholder="referrer@email.com"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Anything else you'd like us to know?
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your audience, how you plan to refer, etc."
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                style={{ width: '100%' }}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Apply Now'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}

export default ReferralPage;
